import {
  Avatar,
  Badge,
  Box,
  Button,
  Container,
  Grid,
  Group,
  Loader,
  Paper,
  Rating,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import {
  IconBrandInstagram,
  IconBrandTiktok,
  IconBrandTwitter,
  IconBrandYoutube,
  IconCash,
  IconEdit,
  IconHandGrab,
  IconMail,
  IconTag,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { User } from "../../../gql/__generated__/graphql";
import { userInfoAtom } from "../../atoms";
import TikTokLoginButton from "../../components/TikTokLoginButton/TikTokLoginButton";
import ProfileEditForm from "./components/ProfileEditForm";
import { useProfile } from "./components/useProfile";

const ProfilePage = () => {
  const { id } = useParams<{
    id: string;
  }>();
  // React State
  const [isMyProfile, setIsMyProfile] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Recoil State
  const userInfo = useRecoilValue(userInfoAtom);

  const { profile, loading, updateProfile } = useProfile(id);

  useEffect(() => {
    if (userInfo?.email === id) {
      setIsMyProfile(true);
    }
  }, []);

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleProfileUpdate = (updatedProfile: User) => {
    updateProfile(updatedProfile);
    setIsEditing(false);
  };

  if (loading) {
    return (
      <Container className="center" size="xl">
        <Loader />
      </Container>
    );
  }

  if (!profile) {
    return (
      <Container size="xl">
        <Box ta="center" py="xl">
          <Text>Profile not found.</Text>
        </Box>
      </Container>
    );
  }

  return (
    <Box>
      <Container size="xl">
        <Stack gap="xl">
          {/* Toggle for demo purposes */}
          <Group justify="flex-end">
            <Switch
              label="View as my profile"
              checked={isMyProfile}
              onChange={(event) => setIsMyProfile(event.currentTarget.checked)}
            />
          </Group>

          {isEditing ? (
            <ProfileEditForm
              profile={profile}
              onSave={handleProfileUpdate}
              onCancel={() => setIsEditing(false)}
              role={profile.userType}
            />
          ) : (
            <>
              {/* Profile Header */}
              <Paper shadow="sm" p="md" radius="md" withBorder>
                <Group wrap="nowrap" align="flex-start">
                  <Box pos="relative">
                    <Avatar
                      src={profile.imageUrl}
                      size={140}
                      radius={profile.userType === "BRAND_USER" ? "md" : "xl"}
                      style={{
                        border:
                          profile.userType === "BRAND_USER"
                            ? "3px solid var(--mantine-color-primary-6)"
                            : "3px solid var(--mantine-color-primary-6)",
                      }}
                    />
                    {/* Role Badge - Prominent positioning */}
                    <Badge
                      color={
                        profile.userType === "BRAND_USER"
                          ? "primary"
                          : "primary"
                      }
                      size="xl"
                      variant="filled"
                      pos="absolute"
                      bottom={-10}
                      left="50%"
                      style={{
                        transform: "translateX(-50%)",
                        fontWeight: 700,
                        fontSize: "14px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        minWidth: "80px",
                        height: "28px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {profile.userType === "BRAND_USER" ? "Brand" : "Creator"}
                    </Badge>
                  </Box>

                  <Box style={{ flex: 1 }}>
                    <Group justify="space-between" wrap="nowrap">
                      <Box>
                        <Title
                          order={1}
                          style={(theme) => ({
                            background:
                              profile.userType === "BRAND_USER"
                                ? `linear-gradient(45deg, ${theme.colors.primary[6]}, ${theme.colors.tertiary[6]})`
                                : `linear-gradient(45deg, ${theme.colors.primary[6]}, ${theme.colors.secondary[6]})`,
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                          })}
                        >
                          {profile.name}
                        </Title>
                        <Group gap="xs" mt="xs">
                          {profile.categories?.map((category, index) => (
                            <Badge
                              key={index}
                              color={
                                profile.userType === "BRAND_USER"
                                  ? "tertiary"
                                  : "secondary"
                              }
                              size="md"
                              variant="light"
                              leftSection={<IconTag size={14} />}
                            >
                              {category}
                            </Badge>
                          ))}
                        </Group>
                      </Box>
                      {isMyProfile && (
                        <Group gap={"sm"}>
                          <TikTokLoginButton />
                          <Button
                            leftSection={<IconEdit size={16} />}
                            color={
                              profile.userType === "BRAND_USER"
                                ? "primary"
                                : "primary"
                            }
                            onClick={handleEditToggle}
                          >
                            Edit Profile
                          </Button>
                        </Group>
                      )}
                    </Group>

                    <Group mt="md" align="center">
                      <Rating
                        value={profile.rating || 0}
                        readOnly
                        fractions={2}
                        color={
                          profile.userType === "BRAND_USER"
                            ? "primary"
                            : "primary"
                        }
                      />
                      <Text size="sm">(0 reviews)</Text>
                    </Group>

                    {profile.userType === "CREATOR" && profile.socialMedia && (
                      <Group mt="md">
                        {profile.socialMedia.instagram && (
                          <Group gap="xs">
                            <IconBrandInstagram size={20} color="#E1306C" />
                            <Text size="sm">
                              {profile.socialMedia.instagram.toLocaleString()}{" "}
                              followers
                            </Text>
                          </Group>
                        )}
                        {profile.socialMedia.tiktok && (
                          <Group gap="xs">
                            <IconBrandTiktok size={20} color="#000000" />
                            <Text size="sm">
                              {profile.socialMedia.tiktok.toLocaleString()}{" "}
                              followers
                            </Text>
                          </Group>
                        )}
                        {profile.socialMedia.youtube && (
                          <Group gap="xs">
                            <IconBrandYoutube size={20} color="#FF0000" />
                            <Text size="sm">
                              {profile.socialMedia.youtube.toLocaleString()}{" "}
                              subscribers
                            </Text>
                          </Group>
                        )}
                        {profile.socialMedia.twitter && (
                          <Group gap="xs">
                            <IconBrandTwitter size={20} color="#1DA1F2" />
                            <Text size="sm">
                              {profile.socialMedia.twitter.toLocaleString()}{" "}
                              followers
                            </Text>
                          </Group>
                        )}
                      </Group>
                    )}
                  </Box>
                </Group>
              </Paper>

              {/* Profile Tabs */}
              <Tabs defaultValue="about" variant="outline">
                <Tabs.List>
                  <Tabs.Tab value="about">About</Tabs.Tab>
                  <Tabs.Tab value="reviews">Reviews (0)</Tabs.Tab>
                  {profile.userType === "BRAND_USER" && (
                    <Tabs.Tab value="products">Products (0)</Tabs.Tab>
                  )}
                  {profile.userType === "CREATOR" && (
                    <Tabs.Tab value="portfolio">Portfolio (0)</Tabs.Tab>
                  )}
                </Tabs.List>

                {/* About Tab */}
                <Tabs.Panel value="about" pt="xl">
                  <Grid>
                    <Grid.Col span={{ base: 12, md: 8 }}>
                      <Stack>
                        <Paper shadow="xs" p="md" withBorder>
                          <Title order={3} mb="md">
                            Description
                          </Title>
                          <Text>{profile.description}</Text>
                        </Paper>
                      </Stack>
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, md: 4 }}>
                      <Stack>
                        <Paper shadow="xs" p="md" withBorder>
                          <Title order={3} mb="md">
                            Contact Information
                          </Title>
                          <Stack gap="sm">
                            {profile.email && (
                              <Group gap="xs">
                                <IconMail size={18} />
                                <Text>{profile.email}</Text>
                              </Group>
                            )}
                          </Stack>
                        </Paper>

                        {profile.userType === "CREATOR" && (
                          <Paper shadow="xs" p="md" withBorder>
                            <Title order={3} mb="md">
                              Creator Stats
                            </Title>
                            <Stack gap="sm">
                              {profile.monthlyGMV && (
                                <Group gap="xs">
                                  <IconCash size={18} />
                                  <Text>
                                    ${profile.monthlyGMV.toLocaleString()}/mo
                                    GMV
                                  </Text>
                                </Group>
                              )}
                              {profile.completedCollabs && (
                                <Group gap="xs">
                                  <IconHandGrab size={18} />
                                  <Text>
                                    {profile.completedCollabs} Completed Collabs
                                  </Text>
                                </Group>
                              )}
                            </Stack>
                          </Paper>
                        )}
                      </Stack>
                    </Grid.Col>
                  </Grid>
                </Tabs.Panel>

                {/* Reviews Tab */}
                <Tabs.Panel value="reviews" pt="xl">
                  <Stack>
                    <Text>No reviews yet.</Text>
                  </Stack>
                </Tabs.Panel>

                {/* Products Tab (for Brands) */}
                {profile.userType === "BRAND_USER" && (
                  <Tabs.Panel value="products" pt="xl">
                    <SimpleGrid
                      cols={{ base: 1, sm: 2, md: 3, lg: 4 }}
                      spacing="md"
                    >
                      <Text>No products yet.</Text>
                    </SimpleGrid>
                  </Tabs.Panel>
                )}

                {/* Portfolio Tab (for Creators) */}
                {profile.userType === "CREATOR" && (
                  <Tabs.Panel value="portfolio" pt="xl">
                    <SimpleGrid
                      cols={{ base: 1, sm: 2, md: 3, lg: 4 }}
                      spacing="md"
                    >
                      <Text>No portfolio items yet.</Text>
                    </SimpleGrid>
                  </Tabs.Panel>
                )}
              </Tabs>
            </>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export default ProfilePage;
